import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CandlestickChart, ExternalLink, Settings2, Loader2, BadgeCheck, X, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import FearGreedGauge from '../components/FearGreedGauge';
import Footer from '../components/Footer';
import TradingViewWidget from '../components/TradingViewWidget';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYMBOLS = [
  { label: 'BTC/USDT', value: 'BINANCE:BTCUSDT' },
  { label: 'ETH/USDT', value: 'BINANCE:ETHUSDT' },
  { label: 'SOL/USDT', value: 'BINANCE:SOLUSDT' },
  { label: 'GOLD', value: 'OANDA:XAUUSD' },
  { label: 'S&P 500', value: 'FOREXCOM:SPXUSD' },
  { label: 'DXY', value: 'TVC:DXY' },
];

const AdminConnectPanel = ({ settings, onSaved, getToken }) => {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState(settings.username || '');
  const [scriptInput, setScriptInput] = useState('');
  const [scriptIds, setScriptIds] = useState(settings.script_ids || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/tradingview`,
        { username, script_ids: scriptIds, default_symbol: settings.default_symbol || 'BINANCE:BTCUSDT' },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      onSaved(res.data);
      toast.success('TradingView settings saved');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addScript = () => {
    const v = scriptInput.trim();
    if (!v) return;
    // Accept raw PUB;xxx ids or full script URLs
    let id = v;
    const m = v.match(/script\/([A-Za-z0-9]+)/);
    if (m) id = `PUB;${m[1]}`;
    if (!id.startsWith('PUB;') && !id.startsWith('STD;')) id = `PUB;${id}`;
    if (!scriptIds.includes(id)) setScriptIds([...scriptIds, id]);
    setScriptInput('');
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} variant="outline"
        className="border-[#C5A059]/40 bg-transparent text-[#C5A059] hover:bg-[#C5A059]/10 rounded-sm text-xs"
        data-testid="connect-tradingview-btn">
        <Settings2 size={14} className="mr-2" /> Connect TradingView
      </Button>
    );
  }

  return (
    <div className="bg-[#111111] border border-[#C5A059]/30 rounded-xl p-6 w-full" data-testid="tradingview-admin-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-sm">Connect Your TradingView Account</h3>
        <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white" data-testid="close-admin-panel">
          <X size={16} />
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-gray-400 block mb-1.5">TradingView Username</label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. DigitalGoldGang"
            className="bg-black/50 border-white/10 text-white h-10"
            data-testid="tv-username-input" />
          <p className="text-[11px] text-gray-600 mt-1">Links your profile & published ideas on this page.</p>
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1.5">Published Indicator / Strategy Scripts</label>
          <div className="flex gap-2">
            <Input value={scriptInput} onChange={(e) => setScriptInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addScript()}
              placeholder="Paste published script URL or ID"
              className="bg-black/50 border-white/10 text-white h-10"
              data-testid="tv-script-input" />
            <Button onClick={addScript} variant="outline" className="border-white/20 text-gray-300 h-10" data-testid="tv-add-script-btn">
              <Plus size={14} />
            </Button>
          </div>
          <p className="text-[11px] text-gray-600 mt-1">
            Only <span className="text-gray-400">publicly published</span> Pine Scripts can load on embedded charts (TradingView restriction).
            Private indicators must be published first.
          </p>
          {scriptIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {scriptIds.map(id => (
                <span key={id} className="inline-flex items-center gap-1 bg-[#C5A059]/10 border border-[#C5A059]/30 text-[#C5A059] text-xs px-2 py-1 rounded-full">
                  {id}
                  <button onClick={() => setScriptIds(scriptIds.filter(s => s !== id))} className="hover:text-white">
                    <Trash2 size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <Button onClick={save} disabled={saving}
          className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm font-semibold w-full"
          data-testid="tv-save-settings-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
          Save Connection
        </Button>
      </div>
    </div>
  );
};

export default function LiveChart() {
  const { user, getToken } = useAuth();
  const [settings, setSettings] = useState(null);
  const [symbol, setSymbol] = useState('BINANCE:BTCUSDT');

  useEffect(() => {
    axios.get(`${API}/settings/tradingview`)
      .then(r => {
        setSettings(r.data);
        if (r.data.default_symbol) setSymbol(r.data.default_symbol);
      })
      .catch(() => setSettings({ username: '', script_ids: [], default_symbol: 'BINANCE:BTCUSDT' }));
  }, []);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="live-chart-page">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
                <CandlestickChart size={20} className="text-black" />
              </div>
              <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
                Live <span className="text-[#C5A059]">Markets</span>
              </h1>
              <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs px-3 py-1 rounded-full font-medium">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                LIVE 24/7
              </span>
            </div>
            <p className="text-gray-400 text-sm max-w-2xl">
              Real-time TradingView chart with full drawing tools, market heatmap and economic calendar — all in one place.
            </p>
          </div>
          {isAdmin && settings && (
            <AdminConnectPanel settings={settings} getToken={getToken}
              onSaved={(s) => setSettings(s)} />
          )}
        </div>

        {/* Fear & Greed compact widget */}
        <div className="mb-6">
          <FearGreedGauge compact />
        </div>

        {/* Symbol tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="chart-symbol-tabs">
          {SYMBOLS.map(s => (
            <button key={s.value} onClick={() => setSymbol(s.value)}
              data-testid={`chart-symbol-${s.label.replace(/[^A-Za-z0-9]/g, '')}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                symbol === s.value
                  ? 'bg-[#C5A059] text-black border-[#C5A059]'
                  : 'bg-[#111111] text-gray-300 border-white/10 hover:border-[#C5A059]/50'
              }`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden mb-6">
          {settings ? (
            <TradingViewWidget symbol={symbol} studies={settings.script_ids || []} height={640} />
          ) : (
            <div className="flex items-center justify-center h-[640px]">
              <Loader2 size={28} className="animate-spin text-[#C5A059]" />
            </div>
          )}
        </div>

        {/* Share + Profile row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <ShareButtons title="Watch live markets on DigitalGoldGang" />
        </div>

        {/* TradingView profile card */}
        {settings?.username ? (
          <div className="bg-gradient-to-b from-[#151515] to-[#0D0D0D] border border-[#C5A059]/30 rounded-xl p-6" data-testid="tv-profile-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#C5A059]/15 flex items-center justify-center">
                  <BadgeCheck size={24} className="text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">@{settings.username} on TradingView</h3>
                  <p className="text-gray-500 text-sm">Follow for published ideas, setups and strategy updates.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href={`https://www.tradingview.com/u/${settings.username}/#published-charts`}
                  target="_blank" rel="noopener noreferrer" data-testid="tv-ideas-link">
                  <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm font-semibold">
                    View Published Ideas <ExternalLink size={14} className="ml-2" />
                  </Button>
                </a>
                <a href={`https://www.tradingview.com/u/${settings.username}/`}
                  target="_blank" rel="noopener noreferrer" data-testid="tv-profile-link">
                  <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5 rounded-sm">
                    Full Profile <ExternalLink size={14} className="ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        ) : isAdmin ? (
          <div className="bg-[#111111] border border-dashed border-white/20 rounded-xl p-6 text-center" data-testid="tv-not-connected">
            <p className="text-gray-500 text-sm">
              No TradingView account connected yet. Use <span className="text-[#C5A059]">Connect TradingView</span> above
              to link your profile and load your published indicators onto this chart.
            </p>
          </div>
        ) : null}

        {/* Market Overview widgets (merged from Live Markets) */}
        <div className="mt-10" data-testid="market-overview-section">
          <h2 className="text-white font-manrope font-bold text-2xl mb-5">Market <span className="text-[#C5A059]">Overview</span></h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/5">
                <h3 className="text-white font-semibold text-sm">Crypto Market Heatmap</h3>
              </div>
              <iframe
                src="https://www.tradingview.com/embed-widget/crypto-coins-heatmap/?locale=en#%7B%22dataSource%22%3A%22Crypto%22%2C%22blockSize%22%3A%22market_cap_calc%22%2C%22blockColor%22%3A%22change%22%2C%22colorTheme%22%3A%22dark%22%2C%22hasTopBar%22%3Afalse%2C%22isDataSetEnabled%22%3Afalse%2C%22isZoomEnabled%22%3Atrue%2C%22hasSymbolTooltip%22%3Atrue%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%7D"
                style={{ width: '100%', height: '480px', border: 'none' }}
                title="Crypto Heatmap"
                data-testid="crypto-heatmap"
              />
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/5">
                <h3 className="text-white font-semibold text-sm">Economic Calendar</h3>
              </div>
              <iframe
                src="https://www.tradingview.com/embed-widget/events/?locale=en#%7B%22colorTheme%22%3A%22dark%22%2C%22isTransparent%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22importanceFilter%22%3A%22-1%2C0%2C1%22%2C%22currencyFilter%22%3A%22USD%2CEUR%2CGBP%2CJPY%2CCNY%22%7D"
                style={{ width: '100%', height: '480px', border: 'none' }}
                title="Economic Calendar"
                data-testid="economic-calendar"
              />
            </div>
          </div>
        </div>

        <p className="text-[11px] text-gray-600 mt-6">
          Chart powered by TradingView. Market data shown for educational purposes only — not financial advice.
        </p>
      </div>
      <Footer />
    </div>
  );
}
